/**
 * Prioridad de un elemento del Learning Bank.
 *
 * DECISIÓN D (aprobada 2026-08-27): el usuario nunca debe ver un 1/2/3 sin
 * saber si 1 es lo más o lo menos importante.
 *
 * Se almacena como cadena, no como número. No hay ninguna razón técnica para
 * usar números —Dexie indexa cadenas igual de bien— y una cadena elimina la
 * ambigüedad de raíz: `'alta'` no puede malinterpretarse, `1` sí. Además el
 * JSON exportado queda legible sin necesitar una leyenda.
 *
 * La semántica vive SOLO aquí. Ninguna otra parte del proyecto puede definir
 * su propia escala ni su propio orden.
 *
 * LA PRIORIDAD ES SIEMPRE MANUAL. Ninguna regla la calcula, y no entra como
 * segunda caja negra al lado de la del Día 5.
 */

export const PRIORIDADES = ['alta', 'media', 'baja'] as const;

export type Prioridad = (typeof PRIORIDADES)[number];

/** Lo que ve el usuario. Nunca se muestra la clave interna. */
export const NOMBRE_PRIORIDAD: Record<Prioridad, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

/**
 * Orden para listar: menor número primero.
 *
 * Es el ÚNICO lugar donde se define qué prioridad va antes que cuál. Cualquier
 * ordenación del banco usa esto; nadie compara cadenas por su orden alfabético,
 * que daría alta → baja → media.
 */
export const ORDEN_PRIORIDAD: Record<Prioridad, number> = {
  alta: 0,
  media: 1,
  baja: 2,
};

/** Prioridad por defecto de un elemento nuevo. */
export const PRIORIDAD_POR_DEFECTO: Prioridad = 'media';

export function esPrioridad(valor: unknown): valor is Prioridad {
  return typeof valor === 'string' && (PRIORIDADES as readonly string[]).includes(valor);
}

/** Comparador para ordenar elementos por prioridad, de más a menos urgente. */
export function compararPorPrioridad(a: Prioridad, b: Prioridad): number {
  return ORDEN_PRIORIDAD[a] - ORDEN_PRIORIDAD[b];
}
